<template>
  <div class="your-responsibility-checklist-tab">
    <div v-if="showBreadcrumb" class="breadcrumb-container">
      <ExBreadcrumbs :breadcrumbsList="breadcrumbItems" @onClickBreadcrumb="handleBreadcrumbClick" />
    </div>
    <AsEmployeeResponsibilityAssignmentHeader
      :responsibility="responsibility"
      :responsibilityName="responsibilityName"
      :roleName="roleName"
      :positionName="positionName"
      :assignedOn="assignedOn"
      :availableInspectors="availableInspectors"
      :inspectionStatus="inspectionStatus"
      :checkpointCounts="checkpointCounts"
      :currentBadgeStatus="currentBadgeStatus"
      :readinessLevel="readinessLevel"
      :readinessDot="readinessDot"
      class="assignment-header"
    />
    
    <EmployeeHorizontalMenu
      :menuItems="menuItems"
      :initialTab="selectedTab"
      @tab-selected="handleTabSelected"
    />
    
    <YourResponsibilityChecklistTable
      v-if="selectedTab === 'Checklist'"
      :data="checkpointsData"
      :pagination="pagination"
      :levelCounter="levelCounter"
      @appliedFilter="handleAppliedFilter"
      @filtersChanged="handleFiltersChanged"
      @paginationChanged="handlePaginationChanged"
    />
    
    <div v-else-if="selectedTab === 'Available Inspectors'" class="">
      <ResponsibilityAvailableInspectorsCards
        :availableInspectors="availableInspectors"
        :showInspectorsHeader="true"
      />
    </div>
    
    <div v-else-if="selectedTab === 'Purpose'" class="responsibility-purpose-tab">
      <PurposeViewEdit
        :value="responsibilityPurpose"
        :readOnly="true"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import AsEmployeeResponsibilityAssignmentHeader from '@components/AsEmployeeResponsibilityAssignmentHeader/AsEmployeeResponsibilityAssignmentHeader.vue';
import YourResponsibilityChecklistTable from '@components/YourResponsibilityChecklistTable/YourResponsibilityChecklistTable.vue';
import EmployeeHorizontalMenu from '@components/EmployeeHorizontalMenu/EmployeeHorizontalMenu.vue';
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import ResponsibilityAvailableInspectorsCards from '@components/ResponsibilityAvailableInspectorsCards/ResponsibilityAvailableInspectorsCards.vue';
import PurposeViewEdit from '@components/PurposeViewEdit/PurposeViewEdit.vue';
// Props
const props = defineProps({
  responsibility: {
    type: Object,
    default: () => ({
      responsibilityCode: '',
      responsibilityImportance: '',
      roleCode: '',
      roleImportance: '',
      positionCode: '',
      positionImportance: '',
      positionCommitment: '',
      newDuration: '',
      show_codes: false
    })
  },
  responsibilityName: {
    type: String,
    default: ''
  },
  roleName: {
    type: String,
    default: ''
  },
  positionName: {
    type: String,
    default: ''
  },
  assignedOn: {
    type: String,
    default: ''
  },
  availableInspectors: {
    type: Array,
    default: () => []
  },
  inspectionStatus: {
    type: Array,
    default: () => []
  },
  checkpointCounts: {
    type: Object,
    default: () => ({})
  },
  currentBadgeStatus: {
    type: String,
    default: ''
  },
  readinessLevel: {
    type: String,
    default: 'Apprentice'
  },
  readinessDot: {
    type: String,
    default: 'No Dot'
  },
  checkpointsData: {
    type: Array,
    default: () => []
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10
    })
  },
  checklistCount: {
    type: Number,
    default: 0
  },
  availableInspectorsCount: {
    type: Number,
    default: 0
  },
  levelCounter: {
    type: Object,
    default: () => ({
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0
    })
  },
  showBreadcrumb: {
    type: Boolean,
    default: false
  },
  initialTab: {
    type: String,
    default: 'Checklist'
  },
  purpose: {
    type: String,
    default: ''
  },
  responsibilityPurpose: {
    type: String,
    default: ''
  }
});

// Emits
const emit = defineEmits(['appliedFilter', 'filtersChanged', 'tab-selected', 'onBackToTable', 'paginationChanged']);

// State
const selectedTab = ref(props.initialTab);

// Computed
const menuItems = computed(() => [
  {
    label: 'Checklist',
    icon: '',
    enabled: true,
    badge: props.checklistCount || props.checkpointsData.length
  },
  {
    label: 'Available Inspectors',
    icon: '',
    enabled: true,
    badge: props.availableInspectorsCount || props.availableInspectors.length
  },
  {
    label: 'Purpose',
    icon: '',
    enabled: true
  }
]);

const breadcrumbItems = computed(() => [
  {
    text: 'Your Responsibilities',
    disabled: false,
  },
  {
    text: 'Responsibility Assignment',
    disabled: true,
  }
]);
// Methods
function handleAppliedFilter(filters) {
  emit('appliedFilter', filters);
}

function handleFiltersChanged(filterData) {
  emit('filtersChanged', filterData);
}

function handleTabSelected(tabLabel) {
  selectedTab.value = tabLabel;
  emit('tab-selected', tabLabel);
}
function handleBreadcrumbClick(breadcrumb) {
  emit('onBackToTable');
}

function handlePaginationChanged(paginationData) {
  emit('paginationChanged', paginationData);
}

// Watch for changes to initialTab prop
watch(() => props.initialTab, (newTab) => {
  if (newTab) {
    selectedTab.value = newTab;
  }
});
</script>

<style scoped>
.your-responsibility-checklist-tab {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.tab-content {
  min-height: 200px;
}

.coming-soon {
  text-align: center;
  color: #667085;
  font-size: 16px;
  font-weight: 500;
  padding: 60px 20px;
}
.assignment-header {
  padding: 0 16px;
}
.breadcrumb-container {
  padding: 0 16px 15px 16px;
}
.responsibility-purpose-tab {
  padding: 0px 24px 24px 24px;
  background-color: #FFFFFF;
}
</style>

